/**
 * Push infra Lane 1 — native APNs registration wiring (contract §5.2/§5.3/§6).
 *
 * The Capacitor shell's bridge to the server device registry. Mirrors
 * healthKit.ts: native-only, best-effort, NEVER throws into the app, and the
 * plugin is bound by jsName via @capacitor/core so the WEB build (and tests)
 * never depend on @capacitor/push-notifications' JS.
 *
 * INERT UNTIL OPT-IN (§6): there is NO first-launch prompt. Registration only
 * runs when `requestAndRegisterNativePush` is called from the CONTEXTUAL ask
 * (recommended: right after the first coach reaction appears), and only when
 * the `push_notifications_v1` flag is ON (the caller passes `flagOn`) and we're
 * inside the native shell. On web / flag-off it is a pure no-op — no plugin
 * bind, no permission prompt, no network.
 *
 * The stable identity is an app-generated `installation_id` (S1), created once
 * and kept in Preferences. A token refresh re-fires the 'registration' event →
 * we re-POST the SAME installation_id → the server updates the SAME row (no
 * churn). Logout calls `unbindNativePush` → the server deactivates the row.
 */

import { Preferences } from '@capacitor/preferences';
import { isNativeApp } from './platform';
import { apiUrl } from './apiBase';
import { sanitizeInternalHref } from './notifAllowlist';
import type { AuthFetchFn } from '../hooks/useAuthFetch';

// The app bundle id = the Capacitor appId (capacitor.config.ts). It is the
// APNs topic the server stamps on every push (§5.5). Kept in one place.
export const APP_BUNDLE_ID = 'run.tayro.app';

const INSTALL_ID_KEY = 'tayro_push_installation_id';

/**
 * APNs environment (§5.4): local dev builds = sandbox; TestFlight + App Store =
 * PRODUCTION. Baked at build time via VITE_APNS_ENV; defaults to 'production'
 * (the store/TestFlight case) so a mis-set dev build surfaces as BadDeviceToken
 * rather than silently targeting the wrong APNs host.
 */
export function resolveApnsEnvironment(): 'sandbox' | 'production' {
  return (import.meta.env.VITE_APNS_ENV as string) === 'sandbox' ? 'sandbox' : 'production';
}

/** A cross-runtime uuid (browser crypto in the shell; fallback for older WKWebViews). */
function newUuid(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Get (or create + persist) the stable installation id (§5.1 S1). */
export async function ensureInstallationId(): Promise<string> {
  const { value } = await Preferences.get({ key: INSTALL_ID_KEY });
  if (value) return value;
  const id = newUuid();
  await Preferences.set({ key: INSTALL_ID_KEY, value: id });
  return id;
}

/** The exact server payload for POST /api/push/register-device (pure, tested). */
export function buildRegistrationBody(args: {
  installationId: string;
  apnsToken: string;
  environment: 'sandbox' | 'production';
  bundleId: string;
  deviceName?: string | null;
}) {
  return {
    installationId: args.installationId,
    apnsToken: args.apnsToken,
    environment: args.environment,
    bundleId: args.bundleId,
    platform: 'ios',
    deviceName: args.deviceName ?? null,
  };
}

// ── Plugin binding (native-only, lazy — the healthKit pattern) ────────────────
interface PluginListenerHandle { remove: () => Promise<void>; }
interface PushRegistrationToken { value: string }
interface PushRegistrationError { error: string }
interface PushPermissionStatus { receive: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' }
/** The @capacitor/push-notifications `ActionPerformed` shape — the payload of a
 *  NOTIFICATION TAP. Only the fields the tap router reads are modeled; the
 *  server stamps the in-app destination on `notification.data.href` (the same
 *  custom-data key sw.js reads for the parallel web case). */
interface PushActionPerformed {
  actionId: string;
  notification: { data?: unknown } | null;
}
interface PushNotificationsPlugin {
  requestPermissions(): Promise<PushPermissionStatus>;
  checkPermissions(): Promise<PushPermissionStatus>;
  register(): Promise<void>;
  addListener(event: 'registration', cb: (t: PushRegistrationToken) => void): Promise<PluginListenerHandle>;
  addListener(event: 'registrationError', cb: (e: PushRegistrationError) => void): Promise<PluginListenerHandle>;
  addListener(event: 'pushNotificationActionPerformed', cb: (a: PushActionPerformed) => void): Promise<PluginListenerHandle>;
}

let boundPlugin: PushNotificationsPlugin | null = null;
let listenersWired = false;

/**
 * ⛔ THE BOX, AND WHY IT IS A BOX AND NOT A COMMENT.
 *
 * `registerPlugin` returns a **Proxy** whose `get` trap turns EVERY property
 * read into a native bridge call. Resolving a promise with that proxy triggers
 * **thenable assimilation**: the runtime reads `.then`, the proxy forwards
 * `then` to iOS as a plugin method, iOS answers *"not implemented"* — and
 * **neither continuation ever runs, so the promise never settles.**
 *
 * That is not hypothetical here. It is the mechanism that cost THREE device
 * builds and six adversarial review rounds on Sign in with Apple, and it was
 * invisible to ~5,280 tests because every one of them mocked `@capacitor/core`
 * with a plain object — a plain object is not a thenable-intercepting proxy.
 * See `lib/capacitorPluginProxyDouble.ts` for the faithful double.
 *
 * ⚠️ THIS SITE WOULD HAVE FAILED SILENTLY, which is worse than Apple's did.
 * Push registration is best-effort by design — every failure path here is a
 * `console.warn`. A never-settling `await getPushPlugin()` produces no error, no
 * chip, no log: just an app that quietly never registers for notifications, on
 * the day `push_notifications_v1` flips.
 *
 * The wrapper object is the guard: a bare `return boundPlugin` is now a COMPILE
 * ERROR rather than a comment someone reads past. The Apple lane carried a
 * warning comment about this exact hazard and it was read past six times.
 */
interface BoundPushPlugin {
  push: PushNotificationsPlugin;
}

async function getPushPlugin(): Promise<BoundPushPlugin | null> {
  if (!isNativeApp()) return null;
  if (boundPlugin) return { push: boundPlugin };
  try {
    const { registerPlugin } = await import('@capacitor/core');
    boundPlugin = registerPlugin<PushNotificationsPlugin>('PushNotifications');
    return { push: boundPlugin };
  } catch (err) {
    console.warn('[NativePush] plugin bind failed:', err instanceof Error ? err.message : String(err));
    return null;
  }
}

/**
 * The device's current push-permission state, for the Settings row.
 * `unavailable` = web / non-native / bind failure — the row hides itself.
 * Read-only: never triggers the iOS prompt (that is requestAndRegister's job).
 */
export async function checkNativePushPermission(): Promise<
  'granted' | 'denied' | 'prompt' | 'unavailable'
> {
  const bound = await getPushPlugin();
  if (!bound) return 'unavailable';
  try {
    const status = await bound.push.checkPermissions();
    if (status.receive === 'granted') return 'granted';
    if (status.receive === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'unavailable';
  }
}

/** POST the current token to the server registry (§5.2). Best-effort. */
async function postRegistration(fetchAuthed: AuthFetchFn, token: string): Promise<void> {
  try {
    const installationId = await ensureInstallationId();
    const body = buildRegistrationBody({
      installationId,
      apnsToken: token,
      environment: resolveApnsEnvironment(),
      bundleId: APP_BUNDLE_ID,
    });
    await fetchAuthed(apiUrl('/api/push/register-device'), {
      method: 'POST',
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.warn('[NativePush] register post failed:', err instanceof Error ? err.message : String(err));
  }
}

/**
 * The CONTEXTUAL-ASK entry point (§6). Call AFTER the first valuable coaching
 * moment — NEVER at boot. No-op on web, when the flag is off, or if the plugin
 * can't bind. Requests permission (the single iOS system prompt), and only on
 * grant wires the token listeners + registers. The 'registration' listener also
 * absorbs later token refreshes (same installation → same server row).
 */
export async function requestAndRegisterNativePush(
  fetchAuthed: AuthFetchFn,
  opts: { flagOn: boolean },
): Promise<void> {
  if (!opts.flagOn) return;          // flag off → fully inert
  const bound = await getPushPlugin();
  if (!bound) return;                // web / non-native / bind failure → inert
  const { push } = bound;            // unwrap ONLY here — see the box's docblock
  try {
    if (!listenersWired) {
      await push.addListener('registration', (t) => { void postRegistration(fetchAuthed, t.value); });
      await push.addListener('registrationError', (e) => {
        console.warn('[NativePush] registration error:', e?.error);
      });
      listenersWired = true;
    }
    const perm = await push.requestPermissions();
    if (perm.receive !== 'granted') return;   // no re-pressure on decline (§6)
    await push.register();                     // → fires 'registration' with the token
  } catch (err) {
    console.warn('[NativePush] request/register failed:', err instanceof Error ? err.message : String(err));
  }
}

/** Logout unbind (§5.2 case 4): deactivate this install's server row. Best-effort. */
export async function unbindNativePush(fetchAuthed: AuthFetchFn): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { value: installationId } = await Preferences.get({ key: INSTALL_ID_KEY });
    if (!installationId) return;
    await fetchAuthed(apiUrl('/api/push/unregister-device'), {
      method: 'POST',
      body: JSON.stringify({ installationId }),
    });
  } catch (err) {
    console.warn('[NativePush] unbind failed:', err instanceof Error ? err.message : String(err));
  }
}

// ── Notification TAP routing (§5.3) ───────────────────────────────────────────

/**
 * The in-app destination a tap falls back to when its payload carries no valid
 * href — the SAME '/plan' fallback sw.js uses for the parallel web case. Kept
 * as a named constant so the fallback is asserted identically in both lanes.
 */
export const PUSH_TAP_FALLBACK = '/plan';

/**
 * Extract the basename-relative in-app route from a tap action's payload. Pure
 * and total — safe against a null action, a missing/absent `notification`, and
 * a non-object `data`.
 *
 * The href is UNTRUSTED (server-stored payload) so it is sanitized through the
 * EXACT same `notifAllowlist` guard sw.js mirrors; anything it rejects →
 * `/plan`. The returned path carries NO `/v2` prefix — this is the B-21 rake:
 * sw.js prepends `/v2` because it lives OUTSIDE the SPA router
 * (`openWindow('/v2' + href)`), but an in-app `navigate()` runs UNDER the
 * router basename, which adds `/v2` itself on web (and nothing in the native
 * shell). Double-prefixing would 404.
 */
export function extractTapHref(action: PushActionPerformed | null | undefined): string {
  const data = action?.notification?.data;
  const raw =
    data !== null && typeof data === 'object'
      ? (data as Record<string, unknown>).href
      : undefined;
  return sanitizeInternalHref(raw) ?? PUSH_TAP_FALLBACK;
}

/**
 * Wire the native notification-tap listener (§5.3). On a tap Capacitor fires
 * `pushNotificationActionPerformed`; we resolve the sanitized, basename-relative
 * route and hand it to `onRoute`. Cold-start taps (the tap LAUNCHED the app) are
 * delivered as soon as this listener attaches — the caller queues the route
 * until the router is ready (see `pushTapQueue.ts`).
 *
 * INERT off-native / on a plugin bind or wire failure: resolves to a no-op
 * detach; NEVER throws into the app. Flag-gating is the caller's job (the
 * mount) so this stays a thin bridge. Returns a detach function and NEVER the
 * bare plugin proxy — the §0.7 wrapped-proxy rake: a returned Capacitor proxy
 * would be awaited as a thenable and hang the caller.
 */
export async function registerPushTapListener(
  onRoute: (route: string) => void,
): Promise<() => void> {
  const bound = await getPushPlugin();
  if (!bound) return () => {};
  const { push } = bound;            // unwrap ONLY here — see the box's docblock
  try {
    const handle = await push.addListener('pushNotificationActionPerformed', (action) => {
      try {
        onRoute(extractTapHref(action));
      } catch (err) {
        console.warn(
          '[NativePush] tap route dispatch failed:',
          err instanceof Error ? err.message : String(err),
        );
      }
    });
    return () => {
      void handle.remove();
    };
  } catch (err) {
    console.warn(
      '[NativePush] tap listener wire failed:',
      err instanceof Error ? err.message : String(err),
    );
    return () => {};
  }
}

/** Test seam — reset module state between unit tests. */
export function __resetNativePushForTest(): void {
  boundPlugin = null;
  listenersWired = false;
}
